import type { BeadColor } from '../catalog/types';
import { findNearestIndex, findNearestIndexAmong } from './find-nearest';
import type { Grid, QuantizationResult } from './types';

/**
 * Quantification "plus proche voisin" en espace LAB, distance CIEDE2000.
 *
 * Pour chaque pixel de la grille, on cherche la couleur de la palette qui minimise
 * ΔE2000 et on stocke son index. Algorithme O(N pixels × M couleurs).
 *
 * Cache RGB → index : un pixel rencontré une seconde fois ne déclenche pas
 * une seconde recherche. Speedup massif sur les images redondantes (logos,
 * pixel art, dessins en aplats).
 *
 * `allowed` (Lot P2, optionnel) : restreint la recherche à un sous-ensemble
 * d'indices de la palette (réduction du nombre de couleurs, cf. reduce.ts).
 * Les indices retournés restent des indices de la palette COMPLÈTE.
 *
 * @throws si la palette est vide ou si la grille est incohérente.
 */
export function quantize(
  grid: Grid,
  palette: ReadonlyArray<BeadColor>,
  allowed?: ReadonlyArray<number>,
): QuantizationResult {
  if (palette.length === 0) {
    throw new Error('Quantification impossible : palette vide.');
  }
  const expected = grid.width * grid.height;
  if (grid.pixels.length !== expected) {
    throw new Error(
      `Grille incohérente : ${grid.pixels.length} pixels, attendu ${expected} (${grid.width}×${grid.height}).`,
    );
  }

  const cells: number[] = new Array<number>(expected);
  const counts = new Map<string, number>();
  for (const c of palette) {
    counts.set(c.id, 0);
  }

  // Clé compacte sur 24 bits pour un cache RGB → indice.
  const cache = new Map<number, number>();

  for (let i = 0; i < expected; i++) {
    const pixel = grid.pixels[i];
    if (pixel === undefined) {
      throw new Error(`Pixel manquant à l'index ${i}.`);
    }
    const key = (pixel.r << 16) | (pixel.g << 8) | pixel.b;

    let idx = cache.get(key);
    if (idx === undefined) {
      idx = allowed ? findNearestIndexAmong(pixel, palette, allowed) : findNearestIndex(pixel, palette);
      cache.set(key, idx);
    }
    cells[i] = idx;

    const matched = palette[idx];
    if (matched === undefined) {
      throw new Error(`Index ${idx} hors palette (longueur ${palette.length}).`);
    }
    counts.set(matched.id, (counts.get(matched.id) ?? 0) + 1);
  }

  return {
    width: grid.width,
    height: grid.height,
    cells,
    counts,
  };
}

